import {AsyncDataLoading} from '@frontend/components/AsyncDataLoading'
import {trans} from '@frontend/helpers'
import type {AdPreviewDataForGrid} from '@frontend/modules/public/ads/AdsModuleTypes'
import {AdsList} from '@frontend/modules/public/ads/components/list/AdsList'
import {
    type FilteredItemsLoaderAction,
    useFilteredItemsLoader,
} from '@frontend/modules/public/ads/hooks/useFilteredItemsLoader'
import {useAuthStore} from '@frontend/stores'
import clsx from 'clsx'
import {
    useCallback,
    useEffect,
} from 'react'
import {Badge} from 'swayok-react-mdb-ui-kit/components/Typography/Badge'

interface HomePageAdsSectionProps {
    onAdsLoaded?: (action: FilteredItemsLoaderAction) => void
}

// Список объявлений на главной странице.
export function HomePageAdsSection(props: HomePageAdsSectionProps) {

    const {
        onAdsLoaded,
    } = props

    const {
        loadItems,
        items,
        totalItemsCount,
        isLoading,
        loadingError,
        isAllItemsLoaded,
        onItemDataChange,
    } = useFilteredItemsLoader<AdPreviewDataForGrid>({
        type: 'ads',
        listIdForAnalytics: 'home_page',
        pageLimit: 20,
        onSuccess: onAdsLoaded
            ? (_, action) => onAdsLoaded(action)
            : undefined,
    })

    const loadingUserData = useAuthStore(
        state => state.loadingUserData
    )

    const onLoadMore = useCallback(
        (infiniteScroll?: boolean) => loadItems('next', false, infiniteScroll),
        [loadItems]
    )

    // Загрузка объявлений при монтировании.
    useEffect(
        () => {
            if (!loadingUserData) {
                loadItems('filter')
            }
        },
        [loadingUserData]
    )

    return (
        <section id="home-page-offered-items-section">
            <div
                className={clsx(
                    'd-flex flex-column align-items-stretch justify-content-start',
                    'flex-md-row align-items-md-center'
                )}
            >
                <div className="flex-1 d-flex flex-row align-items-center justify-content-between">
                    <div className="d-flex flex-row align-items-center justify-content-start">
                        <h1 className="my-0 offered-items-list-page-header">
                            {trans().ads.list.home_page_header}
                        </h1>
                        {(totalItemsCount ?? 0) > 0 && (
                            <Badge
                                pill
                                color="red"
                                className="ms-3 fs-6 fw-400 align-middle d-inline-block"
                            >
                                {totalItemsCount}
                            </Badge>
                        )}
                    </div>
                </div>
            </div>
            <div className="flex-container-row mt-3 mt-sm-4">
                <AsyncDataLoading
                    loading={isLoading !== null && isLoading !== 'next'}
                    className="flex-1"
                    error={loadingError}
                    onReload={() => loadItems('filter')}
                    render={() => (
                        <AdsList
                            listUid="home_page"
                            ads={items}
                            isAllAdsLoaded={isAllItemsLoaded}
                            isLoadingMoreItems={isLoading === 'next'}
                            onLoadMore={onLoadMore}
                            gridColumns={{
                                xxs: 2,
                                xs: 2,
                                sm: 2,
                                md: 3,
                                lg: 4,
                                xl: 4,
                                xxl: 4,
                            }}
                            allowPromoBanners
                            presentation="grid"
                            onAdDataChange={onItemDataChange}
                            infiniteScroll
                            disabled={isLoading === 'next'}
                        />
                    )}
                />
            </div>
        </section>
    )
}
